<script lang="ts">
  import { onMount } from "svelte"
  import RegularHoursSection from "@kontextwerk/control-web-frame/components/availabilityPolicies/RegularHoursSection.svelte"
  import {
    normalizeAvailabilityPolicyConfig,
    validateAvailabilityPolicyConfig,
  } from "@kontextwerk/control-web-frame/logic/availabilityPolicies"
  import type { AvailabilityPolicyConfig } from "@kontextwerk/control-web-frame/types/availabilityPolicies"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import {
    fetchCustomerAppointmentAvailability,
    updateCustomerAppointmentAvailability,
  } from "../../../../../../../../../../../../api/kehrblatt/customer-registry/customers"

  const { customerId, canEdit = false } = $props<{
    customerId: string
    canEdit?: boolean
  }>()

  let loading = $state(false)
  let saving = $state(false)
  let error = $state<string | null>(null)
  let draft = $state<AvailabilityPolicyConfig | null>(null)

  const sanitizeAppointmentAvailabilityConfig = (
    value?: AvailabilityPolicyConfig | null,
  ): AvailabilityPolicyConfig => ({
    ...normalizeAvailabilityPolicyConfig(value, { emptyDayDefaultMode: "OPEN" }),
    exclude_german_nationwide_holidays: false,
    closed_dates: [],
    closed_date_titles: {},
  })

  const hasWindows = (value?: AvailabilityPolicyConfig | null) =>
    Boolean(value?.hours?.some((day) => day.windows?.length))

  const normalizedDraft = $derived(sanitizeAppointmentAvailabilityConfig(draft))
  const hasAvailabilityWindows = $derived(hasWindows(normalizedDraft))
  const isRestricted = $derived(
    hasAvailabilityWindows || normalizedDraft.hours.some((day) => day.mode === "CLOSED"),
  )

  const loadAvailability = async () => {
    if (!customerId) return

    loading = true
    error = null
    try {
      const response = await fetchCustomerAppointmentAvailability(customerId)
      draft = sanitizeAppointmentAvailabilityConfig(response.availability)
    } catch (loadError) {
      console.error("customer_appointment_availability_load_failed", loadError)
      error = "Die Verfügbarkeiten konnten nicht geladen werden."
    } finally {
      loading = false
    }
  }

  const saveAvailability = async () => {
    if (!customerId || saving || !canEdit) return

    const normalized = sanitizeAppointmentAvailabilityConfig(draft)
    const validationError = validateAvailabilityPolicyConfig(normalized)

    if (validationError) {
      newNotification({ class: "error", html: validationError })

      return
    }

    saving = true
    try {
      const response = await updateCustomerAppointmentAvailability(customerId, {
        availability: normalized,
      })
      draft = sanitizeAppointmentAvailabilityConfig(response.availability)
      newNotification({ class: "success", html: "Verfügbarkeiten gespeichert." })
    } catch (saveError) {
      console.error("customer_appointment_availability_save_failed", saveError)
      newNotification({
        class: "error",
        html: "Die Verfügbarkeiten konnten nicht gespeichert werden.",
      })
    } finally {
      saving = false
    }
  }

  const handleHoursChange = (value: AvailabilityPolicyConfig) => {
    draft = sanitizeAppointmentAvailabilityConfig(value)
  }

  onMount(() => {
    void loadAvailability()
  })
</script>

<section class="customer-availability">
  <SectionTitle
    title="Verfügbarkeiten"
    brightBackground={true}
    styleOuter="margin-bottom: 1.2rem;"
  />

  <ActionBox
    label="Erlaubte Terminzeiten"
    tag={isRestricted ? "Eingeschränkt" : "Ganztägig"}
    tagColor={isRestricted ? "orange" : "green"}
    tagVariant="soft"
    darkMode={true}
  >
    <div class="availability-content">
      {#if loading}
        <p>Verfügbarkeiten werden geladen…</p>
      {:else if error}
        <p>{error}</p>
        <CTA type="secondaryButton" content="Erneut versuchen" onClick={loadAvailability} />
      {:else}
        {#if !isRestricted}
          <p class="empty-copy">
            Keine Terminzeiten hinterlegt. KehrBlatt behandelt diesen Ansprechpartner als ganztägig
            verfügbar.
          </p>
        {/if}
        {#key customerId}
        <RegularHoursSection
          value={normalizedDraft}
          disabled={saving || !canEdit}
          emptyDayLabel="Ganztägig verfügbar"
          emptyDayDefaultMode="OPEN"
          emptyDayClosedLabel="Ganztägig geschlossen"
          emptyDayOpenLabel="Verfügbar"
          onChange={handleHoursChange}
        />
        {/key}
      {/if}
    </div>

    {#snippet buttonSnippet()}
      <CTA
        type="primaryButton"
        content={saving ? "Speichert..." : "Speichern"}
        disabled={saving || loading || !canEdit}
        onClick={saveAvailability}
      />
    {/snippet}
  </ActionBox>
</section>

<style lang="less">
  .customer-availability {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .availability-content {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    color: var(--text-invers-100);

    :global(.regular-section) {
      color: var(--text-invers-100);
    }

    :global(.weekday-list) {
      gap: 0.25rem;
    }
  }

  .availability-content p {
    margin: 0;
    color: var(--text-invers-100);
    line-height: 1.45;
  }

  .empty-copy {
    padding-left: 0.8rem;
    border-left: 2px solid var(--primary-100);
  }
</style>
